import { useProductItemThemeSettings } from '@providers/ThemeProvider/Provider/ProductItemThemeSettings';
import { objectKeys } from '@utils';

import type { ProductInfoProps } from '@/features/filter-product-list/product-list/product-item/ProductInfo';

import type { ProductCTAPropsMapSettings } from './connectCTAButton';
import { useConnectCTAButton } from './connectCTAButton';
import type { InventoryStatusPropsMapSettings } from './connectInventoryStatus';
import { useConnectInventoryStatus } from './connectInventoryStatus';
import type { ProductPricePropsMapSettings } from './connectProductPrice';
import { useConnectProductPrice } from './connectProductPrice';
import type { ProductRatingPropsMapSettings } from './connectProductRating';
import { useConnectProductRating } from './connectProductRating';
import type { ProductSwatchesPropsMapSettings } from './connectProductSwatches';
import type { ProductTitlePropsMapSettings } from './connectProductTitle';
import { useConnectProductTitle } from './connectProductTitle';
import type { ProductVendorPropsMapSettings } from './connectProductVendor';
import { useConnectProductVendor } from './connectProductVendor';

export type ProductInfoPropsMapSettings = Pick<
  ProductInfoProps,
  'textAlign' | 'orders' | 'padding'
> & {
  elements: {
    title?: ProductTitlePropsMapSettings;
    price?: ProductPricePropsMapSettings;
    vendor?: ProductVendorPropsMapSettings;
    inventoryStatus: InventoryStatusPropsMapSettings;
    swatches?: ProductSwatchesPropsMapSettings;
    cta?: ProductCTAPropsMapSettings;
    rating?: ProductRatingPropsMapSettings;
  };
};

export const useConnectProductInfo = () =>
  // productSwatch: ProductSwatchesProps
  {
    const settings = useProductItemThemeSettings();
    const { productInfo: infoSettings } = settings;

    const { props: titleElementProps } = useConnectProductTitle(settings);
    const { props: priceElementProps } = useConnectProductPrice(settings);
    const { props: vendorElementProps } = useConnectProductVendor(settings);
    const { props: inventoryStatusElementProps } = useConnectInventoryStatus(settings);
    const { props: productRatingProps } = useConnectProductRating(settings, 'info');
    const { props: productCTAProps } = useConnectCTAButton(settings, 'info');

    const { padding, textAlign } = infoSettings || {};

    const productInfoProps: ProductInfoPropsMapSettings = {
      elements: {
        title: titleElementProps,
        price: priceElementProps,
        vendor: vendorElementProps,
        inventoryStatus: inventoryStatusElementProps,
        cta: productCTAProps,
        rating: productRatingProps,
      },
      padding,
      textAlign,
      orders: objectKeys(infoSettings?.elements || {}),
    };

    return {
      props: productInfoProps,
    };
  };
